import { useState, useEffect, useCallback } from "react";
import { AppLayout } from "../../components/AppLayout";
import { NotificationSettingsDialog } from "../../components/NotificationSettingsDialog";
import { WeeklySlotsEditor } from "../../components/WeeklySlotsEditor";
import { MedicoDaysPanel } from "./MedicoDaysPanel";
import { DeletedAccountsTab } from "./DeletedAccountsTab";
import { InviteMediciDialog } from "./InviteMediciDialog";
import { SetPasswordButton } from "./SetPasswordButton";
import { AdminsTab } from "./AdminsTab";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import {
  Clock, Users, Pill, CalendarCheck, Check, X, Search, Pencil,
  ShieldCheck, History, Loader2, UserCog, BarChart3, TrendingDown, Download, Building2, Trash2, BookText, DatabaseBackup, Archive, MapPin, Mail,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from "recharts";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-3">
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="font-heading text-2xl font-800 text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

const ROLE_BADGE = {
  medico: { label: "Medico", cls: "bg-sky-100 text-blue-700" },
  isf: { label: "ISF", cls: "bg-violet-100 text-violet-700" },
};

function DeleteUserButton({ name, onConfirm, testid, compact }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {compact ? (
          <Button size="sm" variant="outline" className="h-8 border-red-200 px-2 text-red-600 hover:bg-red-50" data-testid={testid}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="h-8 gap-1 border-red-200 text-red-600 hover:bg-red-50" data-testid={testid}>
            <Trash2 className="h-3.5 w-3.5" /> Elimina
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminare l'utente?</AlertDialogTitle>
          <AlertDialogDescription>
            Stai per eliminare <strong>{name}</strong>. L'account e i relativi appuntamenti, notifiche e
            proposte di scambio saranno spostati in "Account eliminati", da dove potrai ripristinarli entro
            30 giorni. Le controparti con appuntamenti futuri saranno avvisate.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annulla</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onConfirm} data-testid={`${testid}-confirm`}>
            Elimina
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function AdminDashboard() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [archiveKey, setArchiveKey] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);

  const [stats, setStats] = useState({ pending: 0, medici: 0, isf: 0, appointments: 0 });
  const [pending, setPending] = useState([]);
  const [medici, setMedici] = useState([]);
  const [q, setQ] = useState("");
  const [isf, setIsf] = useState([]);
  const [qIsf, setQIsf] = useState("");
  const [selected, setSelected] = useState(null); // {medico, audit_logs}
  const [loading, setLoading] = useState(false);

  const loadStats = useCallback(async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  }, []);
  const loadPending = useCallback(async () => {
    const { data } = await api.get("/admin/pending");
    setPending(data.users);
  }, []);
  const loadMedici = useCallback(async (query = "") => {
    const { data } = await api.get("/admin/medici", { params: query ? { q: query } : {} });
    setMedici(data.medici);
  }, []);
  const loadIsf = useCallback(async (query = "") => {
    const { data } = await api.get("/admin/isf", { params: query ? { q: query } : {} });
    setIsf(data.isf);
  }, []);

  useEffect(() => { loadStats(); loadPending(); loadMedici(); loadIsf(); }, [loadStats, loadPending, loadMedici, loadIsf]);

  const moderate = async (id, action) => {
    try {
      await api.post(`/admin/users/${id}/${action}`);
      toast.success(action === "approve" ? "Utente approvato" : "Utente rifiutato");
      loadPending(); loadStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Utente eliminato (ripristinabile per 30 giorni)");
      loadPending(); loadStats(); loadMedici(q); loadIsf(qIsf); setArchiveKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openMedico = async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/medici/${id}`);
      setSelected(data);
    } finally { setLoading(false); }
  };

  const exportList = async (role, fmt) => {
    try {
      const res = await api.get("/admin/export", { params: { role, fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `lista-${role}.${fmt}`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch (e) { toast.error("Errore durante l'esportazione"); }
  };

  const downloadGuide = async () => {
    try {
      const res = await api.get("/admin/guide", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Guida-Agenda-ISF-COMO.pdf"; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Download guida avviato");
    } catch (e) { toast.error("Errore durante il download della guida"); }
  };

  const backupNow = async () => {
    setBackupLoading(true);
    try {
      const { data } = await api.post("/admin/backup");
      const day = data.created_at.slice(0, 10);
      for (const [kind, label] of [["isf", "per-informatore"], ["medico", "per-medico"]]) {
        const res = await api.get(`/backup/${data.token}/${kind}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = `appuntamenti-${label}-${day}.xlsx`; document.body.appendChild(a); a.click();
        a.remove(); URL.revokeObjectURL(url);
      }
      toast.success(`Backup generato: ${data.count} appuntamenti`);
    } catch (e) { toast.error("Errore durante la generazione del backup"); }
    setBackupLoading(false);
  };

  return (
    <AppLayout title="Pannello Amministratore" onOpenSettings={() => setSettingsOpen(true)}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-800 text-slate-900">Amministrazione</h1>
          <p className="text-sm text-slate-500">Gestisci registrazioni, medici e disponibilità.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={backupNow} disabled={backupLoading} data-testid="backup-now-btn">
            {backupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />} Genera backup ora
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadGuide} data-testid="download-guide-btn">
            <BookText className="h-4 w-4" /> Guida PDF
          </Button>
        </div>
      </div>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Clock} label="Richieste pendenti" value={stats.pending} color="bg-amber-50 text-amber-600" />
        <StatCard icon={Users} label="Medici attivi" value={stats.medici} color="bg-sky-50 text-blue-700" />
        <StatCard icon={Pill} label="ISF attivi" value={stats.isf} color="bg-violet-50 text-violet-700" />
        <StatCard icon={CalendarCheck} label="Appuntamenti" value={stats.appointments} color="bg-emerald-50 text-emerald-600" />
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Richieste pendenti {stats.pending > 0 && <Badge className="ml-2 bg-amber-500">{stats.pending}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="medici" data-testid="tab-medici">Gestione Medici</TabsTrigger>
          <TabsTrigger value="isf" data-testid="tab-isf">Informatori (ISF)</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report"><BarChart3 className="mr-1.5 h-4 w-4" /> Report</TabsTrigger>
          <TabsTrigger value="deleted" data-testid="tab-deleted"><Archive className="mr-1.5 h-4 w-4" /> Account eliminati</TabsTrigger>
          <TabsTrigger value="admins" data-testid="tab-admins"><ShieldCheck className="mr-1.5 h-4 w-4" /> Amministratori</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-5">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            {pending.length === 0 ? (
              <p className="px-6 py-12 text-center text-sm text-slate-400">Nessuna richiesta in attesa.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pending-table">
                  <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Nominativo</th>
                      <th className="px-4 py-3">Ruolo</th>
                      <th className="px-4 py-3">Dettagli</th>
                      <th className="px-4 py-3">Contatti</th>
                      <th className="px-4 py-3 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((u) => (
                      <tr key={u.id} className="border-b border-slate-100 last:border-0" data-testid={`pending-row-${u.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-600 text-slate-900">{u.nome} {u.cognome}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-600 ${ROLE_BADGE[u.role].cls}`}>
                            {ROLE_BADGE[u.role].label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.role === "medico" ? u.specializzazione : u.azienda}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <p>{u.email}</p>
                          <p className="text-xs text-slate-400">{u.telefono}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => moderate(u.id, "approve")} data-testid={`approve-btn-${u.id}`}>
                              <Check className="mr-1 h-3.5 w-3.5" /> Approva
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" onClick={() => moderate(u.id, "reject")} data-testid={`reject-btn-${u.id}`}>
                              <X className="mr-1 h-3.5 w-3.5" /> Rifiuta
                            </Button>
                            <SetPasswordButton user={u} name={`${u.nome} ${u.cognome}`} testid={`set-password-pending-${u.id}`} compact />
                            <DeleteUserButton name={`${u.nome} ${u.cognome}`} onConfirm={() => deleteUser(u.id)} testid={`delete-pending-${u.id}`} compact />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="medici" className="mt-5">
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
            <span className="mr-1 text-sm font-600 text-slate-700">Scarica anagrafiche:</span>
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => exportList("medici", "csv")} data-testid="export-medici-csv-btn">
              <Download className="h-3.5 w-3.5" /> Medici CSV
            </Button>
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => exportList("medici", "pdf")} data-testid="export-medici-pdf-btn">
              <Download className="h-3.5 w-3.5" /> Medici PDF
            </Button>
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => exportList("isf", "csv")} data-testid="export-isf-csv-btn">
              <Download className="h-3.5 w-3.5" /> ISF CSV
            </Button>
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => exportList("isf", "pdf")} data-testid="export-isf-pdf-btn">
              <Download className="h-3.5 w-3.5" /> ISF PDF
            </Button>
            <Button size="sm" className="ml-auto h-8 gap-1.5 bg-blue-700 hover:bg-blue-800" onClick={() => setInviteOpen(true)} data-testid="invite-medici-btn">
              <Mail className="h-3.5 w-3.5" /> Invita medici
            </Button>
          </div>
          <InviteMediciDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
          <div className="mb-4 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cerca medico, specializzazione, email..." value={q}
                onChange={(e) => { setQ(e.target.value); loadMedici(e.target.value); }}
                className="pl-9" data-testid="admin-medici-search" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {medici.map((m) => (
              <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm" data-testid={`admin-medico-card-${m.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-heading font-700 text-slate-900">Dr. {m.nome} {m.cognome}</p>
                    <p className="text-sm text-blue-700">{m.specializzazione}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500" data-testid={`admin-medico-address-${m.id}`}>
                      <MapPin className="h-3.5 w-3.5 shrink-0" /> {m.indirizzo || <span className="italic text-slate-400">Indirizzo non indicato</span>}
                    </p>
                  </div>
                  <Badge variant={m.status === "approved" ? "default" : "secondary"} className={m.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                    {(USER_STATUS[m.status] || USER_STATUS.rejected).label}
                  </Badge>
                </div>
                <p className="mt-2 text-xs text-slate-400">{m.email}</p>
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={() => openMedico(m.id)} data-testid={`edit-medico-btn-${m.id}`}>
                    <UserCog className="h-4 w-4" /> Modifica
                  </Button>
                  <SetPasswordButton user={m} name={`Dr. ${m.nome} ${m.cognome}`} testid={`set-password-medico-${m.id}`} compact />
                  <DeleteUserButton name={`Dr. ${m.nome} ${m.cognome}`} onConfirm={() => deleteUser(m.id)} testid={`delete-medico-${m.id}`} compact />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="isf" className="mt-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cerca informatore, azienda, email..." value={qIsf}
                onChange={(e) => { setQIsf(e.target.value); loadIsf(e.target.value); }}
                className="pl-9" data-testid="admin-isf-search" />
            </div>
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => exportList("isf", "csv")} data-testid="isf-tab-export-csv">
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => exportList("isf", "pdf")} data-testid="isf-tab-export-pdf">
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {isf.length === 0 ? (
              <p className="col-span-full rounded-lg border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">Nessun informatore trovato.</p>
            ) : isf.map((u) => (
              <div key={u.id} className="rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm" data-testid={`admin-isf-card-${u.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-100 font-heading font-700 text-violet-700">
                      {(u.nome?.[0] || "")}{(u.cognome?.[0] || "")}
                    </div>
                    <div>
                      <p className="font-heading font-700 text-slate-900">{u.nome} {u.cognome}</p>
                      <p className="flex items-center gap-1 text-xs text-slate-500"><Building2 className="h-3 w-3" /> {u.azienda}</p>
                    </div>
                  </div>
                  <Badge className={(USER_STATUS[u.status] || USER_STATUS.rejected).cls}>
                    {(USER_STATUS[u.status] || USER_STATUS.rejected).label}
                  </Badge>
                </div>
                <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
                  <p>{u.email}</p>
                  <p>{u.telefono}</p>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <SetPasswordButton user={u} name={`${u.nome} ${u.cognome}`} testid={`set-password-isf-${u.id}`} />
                  <DeleteUserButton name={`${u.nome} ${u.cognome}`} onConfirm={() => deleteUser(u.id)} testid={`delete-isf-${u.id}`} />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="report" className="mt-5">
          <ReportTab />
        </TabsContent>

        <TabsContent value="deleted" className="mt-5">
          <DeletedAccountsTab key={archiveKey} onRestored={() => { loadStats(); loadMedici(q); loadIsf(qIsf); }} />
        </TabsContent>

        <TabsContent value="admins" className="mt-5">
          <AdminsTab />
        </TabsContent>
      </Tabs>

      <MedicoEditDialog
        data={selected}
        loading={loading}
        onClose={() => setSelected(null)}
        onSaved={(d) => { setSelected(d); loadMedici(q); }}
      />
      <NotificationSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </AppLayout>
  );
}

function MedicoEditDialog({ data, loading, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [holidays, setHolidays] = useState({});
  const [logs, setLogs] = useState([]);
  useEffect(() => { setLogs(data?.audit_logs || []); }, [data]);

  useEffect(() => {
    const start = new Date().toISOString().slice(0, 10);
    const end = `${new Date().getFullYear() + 1}-12-31`;
    api.get("/holidays", { params: { start, end } }).then(({ data }) => {
      const m = {}; data.holidays.forEach((h) => { m[h.date] = h.name; }); setHolidays(m);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (data?.medico) {
      const m = data.medico;
      setForm({
        nome: m.nome || "", cognome: m.cognome || "", specializzazione: m.specializzazione || "",
        telefono: m.telefono || "", indirizzo: m.indirizzo || "", note: m.note || "",
        weekly_slots: m.weekly_slots || {}, max_appointments_per_day: m.max_appointments_per_day || 2,
        unlocked_days: m.unlocked_days || [], admin_blocked_days: m.admin_blocked_days || [],
      });
    } else setForm(null);
  }, [data]);

  if (!data || !form) return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <div className="flex h-40 items-center justify-center">
          {loading && <Loader2 className="h-6 w-6 animate-spin text-blue-700" />}
        </div>
      </DialogContent>
    </Dialog>
  );

  const medico = data.medico;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put(`/admin/medici/${medico.id}`, {
        nome: form.nome, cognome: form.cognome, specializzazione: form.specializzazione,
        telefono: form.telefono, indirizzo: form.indirizzo, note: form.note,
        weekly_slots: form.weekly_slots, max_appointments_per_day: Number(form.max_appointments_per_day),
      });
      toast.success("Profilo medico aggiornato");
      onSaved({ medico: res.medico, audit_logs: res.audit_logs });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="medico-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ShieldCheck className="h-5 w-5 text-blue-700" /> Modifica profilo · Dr. {medico.nome} {medico.cognome}
          </DialogTitle>
          <DialogDescription>Le modifiche vengono tracciate nell'audit log.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nome</Label><Input value={form.nome} onChange={set("nome")} className="mt-1" data-testid="edit-nome" /></div>
              <div><Label>Cognome</Label><Input value={form.cognome} onChange={set("cognome")} className="mt-1" data-testid="edit-cognome" /></div>
            </div>
            <div><Label>Specializzazione</Label><Input value={form.specializzazione} onChange={set("specializzazione")} className="mt-1" data-testid="edit-spec" /></div>
            <div><Label>Telefono</Label><Input value={form.telefono} onChange={set("telefono")} className="mt-1" data-testid="edit-telefono" /></div>
            <div><Label>Indirizzo studio</Label><Input value={form.indirizzo} onChange={set("indirizzo")} className="mt-1" data-testid="edit-indirizzo" /></div>
            <div>
              <Label>Max appuntamenti / giorno</Label>
              <Input type="number" min={1} value={form.max_appointments_per_day} onChange={set("max_appointments_per_day")} className="mt-1 w-28" data-testid="edit-max" />
            </div>
            <div><Label>Note e vincoli</Label><Textarea value={form.note} onChange={set("note")} className="mt-1" rows={3} data-testid="edit-note" /></div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Disponibilità settimanale</Label>
              <WeeklySlotsEditor value={form.weekly_slots} onChange={(v) => setForm({ ...form, weekly_slots: v })} />
            </div>
          </div>
        </div>

        <MedicoDaysPanel medicoId={medico.id} form={form} setForm={setForm} holidays={holidays}
          onAudit={setLogs} />

        <div>
          <Label className="mb-2 flex items-center gap-1.5"><History className="h-4 w-4" /> Audit Log</Label>
          <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="audit-log">
            {logs.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuna modifica registrata.</p>
            ) : (
              logs.map((lg) => (
                <div key={lg.id} className="text-xs text-slate-600">
                  <span className="font-600">{new Date(lg.timestamp).toLocaleString("it-IT")}</span> · {lg.action}
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel-btn">Chiudi</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="edit-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Pencil className="mr-2 h-4 w-4" /> Salva modifiche</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RSummary({ label, value, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-800 ${color}`}>{value}</p>
    </div>
  );
}

const USER_STATUS = {
  approved: { label: "Attivo", cls: "bg-emerald-100 text-emerald-700" },
  pending: { label: "In attesa", cls: "bg-amber-100 text-amber-700" },
  rejected: { label: "Rifiutato", cls: "bg-red-100 text-red-600" },
};

const MONTHLY_MARGIN = { top: 5, right: 10, left: -10, bottom: 0 };
const PER_MEDICO_MARGIN = { top: 5, right: 20, left: 20, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: "#64748B" };
const CATEGORY_TICK = { fontSize: 11, fill: "#334155" };
const TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 };
const RADIUS_TOP = [4, 4, 0, 0];
const RADIUS_NONE = [0, 0, 0, 0];
const RADIUS_RIGHT = [0, 4, 4, 0];
const LEGEND_STYLE = { fontSize: 12 };

function ReportTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/reports").then(({ data }) => setData(data));
  }, []);

  if (!data) return (
    <div className="flex h-60 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
  );

  return (
    <div className="space-y-6" data-testid="report-tab">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <RSummary label="Appuntamenti totali" value={data.summary.totale} color="text-slate-900" />
        <RSummary label="Confermati" value={data.summary.confermati} color="text-emerald-600" />
        <RSummary label="Disdette" value={data.summary.disdette} color="text-red-500" />
        <RSummary label="Tasso disdetta" value={`${data.summary.tasso_disdetta}%`} color="text-amber-600" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 font-heading font-700 text-slate-900">Andamento prenotazioni e disdette</h3>
        <p className="mb-4 text-xs text-slate-500">Ultimi 12 mesi, per data appuntamento.</p>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.monthly} margin={MONTHLY_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="prenotazioni" name="Prenotazioni" fill="#026AA2" radius={RADIUS_TOP} />
              <Bar dataKey="disdette" name="Disdette" fill="#EF4444" radius={RADIUS_TOP} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 font-heading font-700 text-slate-900">Attività per medico</h3>
        <p className="mb-4 text-xs text-slate-500">Numero di appuntamenti per medico.</p>
        {data.per_medico.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Nessun dato disponibile.</p>
        ) : (
          <>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={data.per_medico} margin={PER_MEDICO_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="medico" width={140} tick={CATEGORY_TICK} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="confermati" name="Confermati" stackId="a" fill="#026AA2" radius={RADIUS_NONE} />
                  <Bar dataKey="disdette" name="Disdette" stackId="a" fill="#F59E0B" radius={RADIUS_RIGHT} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-medici-table">
                <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <tr><th className="py-2">Medico</th><th className="py-2">Specializzazione</th><th className="py-2 text-right">Totale</th><th className="py-2 text-right">Confermati</th><th className="py-2 text-right">Disdette</th></tr>
                </thead>
                <tbody>
                  {data.per_medico.map((m) => (
                    <tr key={m.medico} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 font-600 text-slate-900">{m.medico}</td>
                      <td className="py-2 text-slate-500">{m.specializzazione}</td>
                      <td className="py-2 text-right text-slate-700">{m.totale}</td>
                      <td className="py-2 text-right text-emerald-600">{m.confermati}</td>
                      <td className="py-2 text-right text-red-500">{m.disdette}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
